import React from "react";
import * as Icons from "lucide-react";
import Seo from "../components/site/Seo";
import { Container, SectionHeading, CtaBand, Eyebrow } from "../components/site/Ui";
import { SERVICES } from "../data/site";
import { useLang } from "../lib/LangContext";


const reelParts = ["hook", "video idea", "shot list", "text overlay", "caption", "call to action"];
const promoParts = [
  "special offers", "events", "new products", "holiday campaigns",
  "seasonal promotions", "sales", "menu changes", "new services",
];
const reviewParts = ["Google reviews", "Facebook reviews", "customer feedback"];

const extras = {
  "Reel & TikTok Ideas": reelParts,
  "Promotional Content": promoParts,
  "Review Responses": reviewParts,
};

export default function Services() {
  const { tr } = useLang();
  return (
    <>
      <Seo
        title="Services — social media posts, captions, Reels and content calendars"
        description="PostMint services: social media posts, caption writing, monthly content calendars, Reel and TikTok ideas, promotional content and graphics, review responses and campaign ideas."
        path="/services"
      />

      <section className="mesh-warm" data-testid="services-hero">
        <Container className="py-16 lg:py-20">
          <Eyebrow>{tr("Services")}</Eyebrow>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {tr("Everything you need to stay active online.")}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            {tr("Pick a package and we take care of the ideas, the writing and the planning. You stay in control of what goes out.")}
          </p>
        </Container>
      </section>

      <section className="py-16 sm:py-20" data-testid="services-grid">
        <Container>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => {
              const Icon = Icons[s.icon] || Icons.Sparkles;
              const list = extras[s.title];
              return (
                <article
                  key={s.title}
                  data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex flex-col rounded-3xl border border-slate-200 bg-white p-7 soft-shadow transition-transform duration-200 hover:-translate-y-1"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF3EF] text-[#F95738]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h2 className="mt-5 font-display text-xl font-semibold text-slate-900">{tr(s.title)}</h2>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{tr(s.body)}</p>
                  {list && (
                    <ul className="mt-4 flex flex-wrap gap-2">
                      {list.map((item) => (
                        <li
                          key={item}
                          className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              );
            })}
          </div>
        </Container>
      </section>

      <section className="border-y border-slate-200 bg-white py-16" data-testid="services-note">
        <Container>
          <SectionHeading
            align="center"
            title="Not sure which services you need?"
            body="Most businesses start with posts, captions and a content calendar, then add Reels and promotional graphics once they see what works. You can change your package at any time."
          />
        </Container>
      </section>

      <CtaBand testId="services-final-cta-button" />
    </>
  );
}
